import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '@/mocks/movies';

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [paused]);

  const go = (i: number) => setIndex((i + heroSlides.length) % heroSlides.length);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) {
      go(index + (dx < 0 ? 1 : -1));
    }
    touchStartX.current = null;
  };

  return (
    <section
      className="relative w-full h-[560px] md:h-[680px] overflow-hidden bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      aria-label="精选专题轮播"
    >
      {heroSlides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${i === index ? 'opacity-100' : 'opacity-0'}`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            title={`${slide.title} 韩国伦理影视专题`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary-950/60 via-primary-950/45 to-primary-950/70" />
        </div>
      ))}

      <div className="relative z-10 w-full h-full flex items-center justify-center px-6">
        <div className="max-w-3xl text-center">
          <span className="inline-block px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs md:text-sm mb-5">
            {heroSlides[index].tag}
          </span>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 leading-tight">
            {heroSlides[index].title}
          </h1>
          <p className="mt-4 text-base md:text-xl text-background-100/90">{heroSlides[index].subtitle}</p>
          <p className="mt-3 text-sm md:text-base text-background-200/80 max-w-2xl mx-auto leading-relaxed">
            {heroSlides[index].desc}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to={`/category?cat=${heroSlides[index].category}`}
              className="w-full sm:w-auto px-7 py-3 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              浏览该专题
            </Link>
            <Link
              to="/search"
              className="w-full sm:w-auto px-7 py-3 rounded-md bg-background-50/10 text-background-50 border border-background-50/40 text-sm hover:bg-background-50/20 transition-colors whitespace-nowrap"
            >
              搜索影视作品
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(index - 1)}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/30 transition-colors"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/30 transition-colors"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
        {heroSlides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => go(i)}
            className={`h-2 rounded-full transition-all ${i === index ? 'w-8 bg-accent-500' : 'w-2 bg-background-50/50'}`}
            aria-label={`切换到第 ${i + 1} 张`}
          />
        ))}
      </div>
    </section>
  );
}